import Link from 'next/link'
import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { InView } from 'react-intersection-observer'
import debounce from 'lodash/debounce'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'

import ListItem from 'app/InspirationPage/components/revamp/ListItem'
import ListItemLoader from 'app/InspirationPage/components/revamp/ListItemLoader'
import { useAPIGetInspirationList } from 'api/inspirations/useAPIInspirations'
import { useSETrackEvent } from 'app/InspirationPage/hooks/useSETrackEvent'

import type { FC } from 'react'

interface InspirationFeaturedBlockProps
  extends React.HTMLAttributes<HTMLDivElement> {
  categorySlug?: string
  onFirstFetch?: (length: number) => void
}

const InspirationFeaturedBlock: FC<InspirationFeaturedBlockProps> = ({
  categorySlug,
  onFirstFetch,
  ...props
}) => {
  const {
    SETrackCategoryClickIdePraktik,
    SETrackCategoryScrollIdePraktik,
    SETrackExploreClickIdePraktik,
    SETrackExploreScrollIdePraktik,
  } = useSETrackEvent()

  const maxItem = 3

  const list = useAPIGetInspirationList({
    categorySlug,
    contentSources: ['CS_BUKTI_KARYA'],
    limit: maxItem,
  })

  React.useEffect(() => {
    if (list.isSuccess) {
      const length = list?.data?.pages?.[0]?.data?.length
      onFirstFetch?.(length)
    }
  }, [list.isSuccess, list?.data, onFirstFetch])

  const handleClickItemTracker = (
    contentId: string,
    contentLocation: number
  ) => {
    if (!!categorySlug) {
      SETrackCategoryClickIdePraktik({
        contentId,
        contentLocation,
        pageLocation: categorySlug,
      })
    } else {
      SETrackExploreClickIdePraktik({ contentId, contentLocation })
    }
  }

  /**
   * SCROLL TRACKER
   */
  const [inViewItems, setInViewItems] = useState<
    Array<{
      contentId: string
      contentPage: number
    }>
  >([])

  // This effect will be called in every `inView` events
  useEffect(() => {
    if (inViewItems.length > 0) {
      handleInViewItemTracker(inViewItems)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewItems])

  // We need to debounce (1 second) the tracker to avoid it being called in every `inView` events.
  const handleInViewItemTracker = useMemo(
    () =>
      debounce((items) => {
        const contentIds = items.map((item) => item.contentId)
        const contentPages = items.map((item) => item.contentPage)

        if (!!categorySlug) {
          SETrackCategoryScrollIdePraktik({
            contentIds,
            contentPages,
            pageLocation: categorySlug,
          })
        } else {
          SETrackExploreScrollIdePraktik({
            contentIds,
            contentPages,
          })
        }

        // Remove the previously sent items
        setInViewItems([])
      }, 1000),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  // END SCROLL TRACKER

  // Display nothing if no data found
  if (!list.isLoading && list?.data?.pages?.[0]?.data?.length === 0) return null

  return (
    <>
      {list.isLoading && <ListItemLoader count={3} />}

      {!list.isLoading && (
        <div className={`bg-gray-10 p-4  ${props?.className}`}>
          <Text variant="heading-sm">Bukti Karya terpilih</Text>
          <div className="mt-2 border border-subdued rounded">
            <div className="divide-y divide-subdued">
              {(list?.data?.pages?.[0]?.data ?? []).map((data, index) => {
                if (index >= maxItem) return null
                const cssClasses = ['bg-white']
                if (index === 0) cssClasses.push('rounded-t')
                if (
                  index === maxItem - 1 ||
                  index === list?.data?.pages?.[0]?.meta?.total - 1
                )
                  cssClasses.push('rounded-b')

                return (
                  <InView
                    as="div"
                    key={index}
                    threshold={1}
                    triggerOnce
                    onChange={(inView) => {
                      if (inView) {
                        setInViewItems((prevItems) =>
                          prevItems.concat({
                            contentId: data.contentId,
                            contentPage: index + 1,
                          })
                        )
                      }
                    }}
                  >
                    <ListItem
                      containerClassName={clsx(cssClasses)}
                      data={data}
                      onClick={() =>
                        handleClickItemTracker(data.contentId, index + 1)
                      }
                    />
                  </InView>
                )
              })}
            </div>
          </div>
          {list?.data?.pages?.[0].meta.total > 3 && (
            <Link
              passHref
              href={
                categorySlug
                  ? `/ide-praktik/kategori/${categorySlug}/bukti-karya`
                  : '/ide-praktik/bukti-karya'
              }
            >
              <Button
                as="a"
                fullWidth
                color="white"
                className="mt-3 !bg-transparent"
              >
                Lihat semua
              </Button>
            </Link>
          )}
        </div>
      )}
    </>
  )
}
export default InspirationFeaturedBlock
